import { SearchIcon } from '@heroicons/react/solid';
import Button from 'components/atoms/Button';
import DateInput from 'components/atoms/DateInput';
import Divider from 'components/atoms/Divider';
import Input from 'components/atoms/Input';
import Accordion from 'components/molecules/Accordion';
import InPageSearchCheckBoxGroup from 'components/molecules/InPageSearchCheckBoxGroup';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'redux/hooks';
import { updateIncidentFilterState } from 'redux/slices/vehicleIncidentsSlice';
import {
	IQueryPeriod,
	initialFilterState,
	IOption,
} from 'types/VehicleIncidents';
import { filtersToQueryStringConverter } from 'utils/filters';
import { useBranches } from 'redux/hooks';

export interface IIncidentSearchBar {
	setFilters:
		| React.Dispatch<React.SetStateAction<string>>
		| ((filters: string) => void);
	setPeriod: React.Dispatch<React.SetStateAction<IQueryPeriod>>;
}

const IncidentSearchBar: React.FC<IIncidentSearchBar> = ({
	setFilters,
	setPeriod,
}) => {
	const dispatch = useDispatch();
	const { branches: branchCache, loading: branchLoading } = useBranches();

	const filters = useAppSelector((state) => state.vehicleIncident.filters);
	const branches = useAppSelector(
		(state) => state.vehicleIncident.basicPermissionsBranches
	);
	const states = useAppSelector(
		(state) => state.vehicleIncident.basicPermissionsStates
	);
	const searchContainerRef = useRef<HTMLDivElement | null>(null);
	const [attemptedNameSearch, setAttemptedNameSearch] = useState(false);
	const nameTargets = (filters.nameTargets ||
		initialFilterState.nameTargets) as IOption[];
	const missingNameTarget = Boolean(
		(filters.name as string)?.trim() &&
		!nameTargets.some((target) => target.checked)
	);

	// Update branch filters when states change
	useEffect(() => {
		if (!branchCache) {
			return;
		}

		// Initialize filters.state if it doesn't exist
		if (!filters.state) {
			dispatch(
				updateIncidentFilterState({
					...filters,
					state: states || [],
					branch: branches || [],
				})
			);
			return;
		}

		// Ensure filters.state is an array
		const stateArray = Array.isArray(filters.state) ? filters.state : [];
		const selectedStates = stateArray.filter((state) => state.checked);

		// Create a map of branch names to their states from the cache
		const branchStateMap = new Map(
			branchCache.map((branch) => [branch.name, branch.state])
		);

		// Create a map of existing branch checked states
		const existingBranchStates = new Map(
			(Array.isArray(filters.branch) ? filters.branch : []).map(
				(branch: IOption) => [branch.title, branch.checked]
			)
		);

		// If no states are selected, show all branches but maintain their checked state
		if (selectedStates.length === 0) {
			const allBranches = branches.map((branch: IOption) => ({
				...branch,
				checked: existingBranchStates.get(branch.title) || false,
			}));

			if (JSON.stringify(allBranches) !== JSON.stringify(filters.branch)) {
				dispatch(
					updateIncidentFilterState({
						...filters,
						branch: allBranches,
					})
				);
			}
			return;
		}

		// Filter branches to only include those in the selected states
		const filteredBranches = branches
			.filter((branch) => {
				const branchState = branchStateMap.get(branch.title);
				if (!branchState) return false;

				return selectedStates.some((state) => state.title === branchState);
			})
			.map((branch: IOption) => ({
				...branch,
				checked: existingBranchStates.get(branch.title) || false,
			}));

		// Only update if the filtered branches are different
		if (JSON.stringify(filteredBranches) !== JSON.stringify(filters.branch)) {
			dispatch(
				updateIncidentFilterState({
					...filters,
					branch: filteredBranches,
				})
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [branchCache, branches, filters.state, states, dispatch]);

	const onClick = () => {
		setAttemptedNameSearch(true);
		if (missingNameTarget) return;
		const query = filtersToQueryStringConverter(filters);
		setFilters(query);
		setPeriod('all');
	};

	const onHandleChange = (
		filterType: string,
		value: React.ChangeEvent<HTMLInputElement> | string | Date
	) => {
		const filterState = JSON.parse(JSON.stringify(filters));
		if (value instanceof Date) {
			// For date filters, set appropriate time bounds
			let dateToStore = value;
			if (filterType === 'from') {
				// Set to start of day (00:00:00.000) in UTC
				dateToStore = new Date(
					Date.UTC(
						value.getFullYear(),
						value.getMonth(),
						value.getDate(),
						0,
						0,
						0,
						0
					)
				);
			} else if (filterType === 'to') {
				// Set to end of day (23:59:59.999) in UTC
				dateToStore = new Date(
					Date.UTC(
						value.getFullYear(),
						value.getMonth(),
						value.getDate(),
						23,
						59,
						59,
						999
					)
				);
			}
			filterState[filterType] = dateToStore.toISOString();
		} else {
			filterState[filterType] = value;
		}

		dispatch(updateIncidentFilterState(filterState));
	};

	const onKeywordKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			onClick();
		}
	};

	const resetFilters = () => {
		setAttemptedNameSearch(false);
		const newFilters = {
			...initialFilterState,
			state: states || [],
			branch: branches || [],
			from: null,
			to: null,
			keyword: '',
		};

		dispatch(updateIncidentFilterState(newFilters));

		// Apply the changes and make a new request (same as apply button)
		const query = filtersToQueryStringConverter(newFilters);
		setFilters(query);
		setPeriod('all');
	};

	if (branchLoading) {
		return <div>Loading branches...</div>;
	}

	const scrollStatusToBottom = () => {
		if (!searchContainerRef.current) {
			return;
		}

		searchContainerRef.current.scrollTo({
			top: searchContainerRef.current.scrollHeight,
			behavior: 'smooth',
		});
	};

	const onStatusToggle = (isOpen: boolean) => {
		if (!isOpen) {
			return;
		}

		scrollStatusToBottom();
	};

	return (
		<>
			<div
				ref={searchContainerRef}
				className="flex flex-col grow overflow-y-scroll border-orange-500 border-t-[1px]"
			>
				<div className="flex flex-col h-full">
					<div className={`flex flex-col w-full justify-center my-4 mx-auto`}>
						<Input
							type="text"
							onChange={(value: string) => {
								onHandleChange('keyword', value);
							}}
							value={filters['keyword'] as string}
							placeholder="Search Incident ID, rego or person involved"
							size="normal"
							className="mt-1"
							Icon={SearchIcon}
							onKeyDown={onKeywordKeyDown}
						/>
					</div>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Date Range" width="w-full" isOpen={true}>
						<div className="w-full grid gap-2 grid-col grid-cols-2">
							<DateInput
								inputLabel="from"
								placeholder="From"
								filterDateType="incident"
								iconClassName="text-secondary"
								selected={
									filters.from ? new Date(filters.from as string) : undefined
								}
							/>
							<DateInput
								inputLabel="to"
								placeholder="To"
								filterDateType="incident"
								iconClassName="text-secondary"
								selected={
									filters.to ? new Date(filters.to as string) : undefined
								}
							/>
						</div>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="State" width="w-full" isOpen={true}>
						<InPageSearchCheckBoxGroup
							filterName="state"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Branch" width="w-full" isOpen={true}>
						<InPageSearchCheckBoxGroup
							filterName="branch"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Severity" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="severity"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Incident Category" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="incidentCategory"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Incident Mechanism" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="incidentMechanism"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Incident Agency" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="incidentAgency"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Psychosocial Agencies" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="psychosocialAgency"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="ICAM" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="icam"
							filterType="incident"
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Notifiable Incident" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="notifiableIncident"
							filterType="incident"
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="MVI Causation" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="mviCausation"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Life Saving Rules" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="lifeSavingRules"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Restricted Injuries" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="restrictedInjury"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Intervention" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="trafficControllersIntervened"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Work Ceased" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="workCeasedUnsafeConditions"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Time Quadrant" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="incidentTimeQuadrant"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Code of Conduct" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="codeOfConduct"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Corrective Actions Severity" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="correctiveActionsPriority"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Corrective Actions" width="w-full">
						<InPageSearchCheckBoxGroup
							filterName="correctiveActions"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion
						title="Status"
						width="w-full"
						onToggle={onStatusToggle}
						onOpenComplete={scrollStatusToBottom}
					>
						<InPageSearchCheckBoxGroup
							filterName="status"
							filterType="incident"
							allCheckBox
							tidyTitles={true}
						/>
					</Accordion>
					<Divider color="border-[#DDDDDD] !border-t-[2px]" />
					<Accordion title="Search by name" width="w-full">
						<Input
							id="incident-name-search"
							label="Name"
							type="text"
							value={(filters.name as string) || ''}
							onChange={(value: string) => onHandleChange('name', value)}
							placeholder="Name"
							size="normal"
							Icon={SearchIcon}
							onKeyDown={onKeywordKeyDown}
							errorMessage={
								attemptedNameSearch && missingNameTarget
									? 'Select at least one name field.'
									: undefined
							}
						/>
						<div className="flex flex-col gap-2 mt-3">
							{nameTargets.map((target) => (
								<label
									key={target.id}
									className={`flex items-start gap-3 p-4 border border-secondary-200 rounded cursor-pointer text-sm text-secondary-900 ${
										target.checked ? 'bg-primary-200' : ''
									}`}
								>
									<input
										type="checkbox"
										checked={target.checked}
										className="h-[18px] w-[18px] shrink-0 mt-0.5 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
										onChange={() =>
											dispatch(
												updateIncidentFilterState({
													...filters,
													nameTargets: nameTargets.map((option) =>
														option.id === target.id
															? { ...option, checked: !option.checked }
															: option
													),
												})
											)
										}
									/>
									<span className="min-w-0 break-words">{target.title}</span>
								</label>
							))}
						</div>
					</Accordion>
					<div className="flex-grow"></div>
				</div>
			</div>
			<div className="flex justify-between w-full mt-auto flex-none shrink border-orange-500 border-t-2">
				<Button
					className="h-16 w-[115px] mx-auto my-6 font-bold tracking-[1.92px] rounded-[29px]"
					onClick={resetFilters}
					type="secondary"
				>
					Clear
				</Button>
				<Button
					className="h-16 w-[212px] mx-auto my-6 font-bold tracking-[1.92px] !rounded-3xl"
					onClick={onClick}
				>
					Apply
				</Button>
			</div>
		</>
	);
};

export default IncidentSearchBar;
